import { lazy, Suspense, Component, useState, useEffect } from 'react'
import { reportClientError } from './lib/reportError'
import { isStaleChunkError, isTransientNetworkError } from './lib/loadErrorClass'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { StepUpProvider } from './contexts/StepUpContext'
import { I18nProvider } from './contexts/I18nContext'
import { ToastProvider } from './contexts/ToastContext'
import { ComposeProvider } from './contexts/ComposeContext'
import { JanetPageProvider } from './contexts/JanetPageContext'
import { JanetObjectProvider } from './contexts/JanetObjectContext'
import { WorkspaceTabsProvider } from './contexts/WorkspaceTabsContext'
import { DemoProvider } from './contexts/DemoContext'
import { RingCentralProvider } from './contexts/RingCentralContext'
import Layout from './components/layout/Layout'

// ── Eagerly loaded (tiny, needed on every navigation) ──
import LoginPage from './pages/LoginPage'
import NotFoundPage from './pages/NotFoundPage'

// ── Code-split page imports (React.lazy) ──
// Each route becomes its own JS chunk, slashing the initial bundle.
const Dashboard              = lazy(() => import('./pages/Dashboard'))
const HomeNorthStar          = lazy(() => import('./pages/HomeNorthStar'))
const ContactsPage           = lazy(() => import('./pages/ContactsPage'))
const CrewDirectoryPage           = lazy(() => import('./pages/CrewDirectoryPage'))
const PersonOfInterestDetailPage  = lazy(() => import('./pages/PersonOfInterestDetailPage'))
const ContactDetailPage      = lazy(() => import('./pages/ContactDetailRoute'))
const CompaniesPage          = lazy(() => import('./pages/CompaniesPage'))
const CompanyDetailPage      = lazy(() => import('./pages/CompanyDetailPage'))
const ActivityPage           = lazy(() => import('./pages/ActivityPage'))
const CalendarPage           = lazy(() => import('./pages/CalendarPage'))
const ClientsPage            = lazy(() => import('./pages/ClientsPage'))
const ClientDetailPage       = lazy(() => import('./pages/ClientDetailPage'))
const PropertiesPage         = lazy(() => import('./pages/PropertiesPage'))
const PropertyDetailPage     = lazy(() => import('./pages/PropertyDetailPage'))
const ProjectsPage           = lazy(() => import('./pages/ProjectsPage'))
const StudioBench            = lazy(() => import('./pages/StudioBench'))
const ProjectDetailPage      = lazy(() => import('./pages/ProjectDetailPage'))
const DraftDetailPage        = lazy(() => import('./pages/DraftDetailPage'))
const SharedNotesPage        = lazy(() => import('./pages/SharedNotesPage'))
const DemoEntryPage          = lazy(() => import('./pages/DemoEntryPage'))
const DemoJourneyPage        = lazy(() => import('./pages/DemoJourneyPage'))
const DealsPage              = lazy(() => import('./pages/DealsPage'))
const DealDetailPage         = lazy(() => import('./pages/DealDetailPage'))
const DealWorkroomPage       = lazy(() => import('./pages/DealWorkroomPage'))
const OpportunitiesPage      = lazy(() => import('./pages/OpportunitiesPage'))
const OpportunityDetailPage  = lazy(() => import('./pages/OpportunityDetailPage'))
const SubmissionsPage        = lazy(() => import('./pages/SubmissionsPage'))
const SubmissionDetailPage   = lazy(() => import('./pages/SubmissionDetailPage'))
const OutboundSubmissionDetailPage = lazy(() => import('./pages/OutboundSubmissionDetailPage'))
const TasksPage              = lazy(() => import('./pages/TasksPage'))
const TaskDetailPage         = lazy(() => import('./pages/TaskDetailPage'))
const FinancePage            = lazy(() => import('./pages/FinancePage'))
const InvoiceDetail          = lazy(() => import('./pages/InvoiceDetail'))
const SettingsPage           = lazy(() => import('./pages/SettingsPage'))
const OnboardingIntakePage   = lazy(() => import('./pages/OnboardingIntakePage'))
const CommsPage              = lazy(() => import('./pages/CommsPage'))
const MessagesPage           = lazy(() => import('./pages/MessagesPage'))
const GoogleContactsImportPage = lazy(() => import('./pages/GoogleContactsImportPage'))
const GoogleContactsPushPage = lazy(() => import('./pages/GoogleContactsPushPage'))
const EmbedThreadPage = lazy(() => import('./pages/EmbedThreadPage'))
const EmbedEventPage = lazy(() => import('./pages/EmbedEventPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const ExtractCompaniesPage   = lazy(() => import('./pages/ExtractCompaniesPage'))
const MergeCompaniesPage     = lazy(() => import('./pages/MergeCompaniesPage'))
const MergeDocumentsPage     = lazy(() => import('./pages/MergeDocumentsPage'))
const BugReportsAdminPage    = lazy(() => import('./pages/BugReportsAdminPage'))
const BugReportDetailPage    = lazy(() => import('./pages/BugReportDetailPage'))
const SuggestionsAdminPage   = lazy(() => import('./pages/SuggestionsAdminPage'))
const AdminUsersPage         = lazy(() => import('./pages/AdminUsersPage'))
const IntelligencePage       = lazy(() => import('./pages/IntelligencePage'))
const ProposalsPage          = lazy(() => import('./pages/ProposalsPage'))
const IndustryDigestPage     = lazy(() => import('./pages/IndustryDigestPage'))
const IndustryTrackerPage    = lazy(() => import('./pages/IndustryTrackerPage'))
const PeopleToKnowPage       = lazy(() => import('./pages/PeopleToKnowPage'))
const ReportsPage            = lazy(() => import('./pages/ReportsPage'))
// ── Creative Console (separate surface; its own layout + datastore) ──
const ConsoleLayout          = lazy(() => import('./console/ConsoleLayout'))
const ConsoleHome            = lazy(() => import('./console/pages/ConsoleHome'))
const StartBrainstormPage    = lazy(() => import('./console/pages/StartBrainstormPage'))
const ConsoleProjectPage     = lazy(() => import('./console/pages/ConsoleProjectPage'))
const PropertyPage           = lazy(() => import('./console/pages/PropertyPage'))
const ConsoleThreadRoom      = lazy(() => import('./console/pages/ConsoleThreadRoom'))
const VisualProjectConsole   = lazy(() => import('./console/visual/VisualProjectConsole'))
const StoryProjectConsole    = lazy(() => import('./console/writing/StoryProjectConsole'))
const StudioProgress         = lazy(() => import('./console/studio/StudioProgress'))
const ReadingRoom            = lazy(() => import('./console/reading/ReadingRoom'))
const GuidesPage             = lazy(() => import('./console/pages/GuidesPage'))
const InboxIntelligencePage  = lazy(() => import('./pages/InboxIntelligencePage'))
const EmailTrackingPage      = lazy(() => import('./pages/EmailTrackingPage'))
const JanetQueuePage         = lazy(() => import('./pages/JanetQueuePage'))
const JanetAttentionPage     = lazy(() => import('./pages/JanetAttentionPage'))
const JanetChatPage          = lazy(() => import('./pages/JanetChatPage'))
const JanetWindowPage        = lazy(() => import('./pages/JanetWindowPage'))
const FilesPage              = lazy(() => import('./pages/FilesPage'))
const JanetInterviewPage     = lazy(() => import('./pages/JanetInterviewPage'))
const EngineeringPage        = lazy(() => import('./pages/EngineeringPage'))
const KnowledgeReviewPage    = lazy(() => import('./pages/KnowledgeReviewPage'))
const JudgmentReviewPage    = lazy(() => import('./pages/JudgmentReviewPage'))
const StudioPage             = lazy(() => import('./pages/StudioPage'))
const DesignBriefPage        = lazy(() => import('./pages/DesignBriefPage'))
const WorkflowRunPage        = lazy(() => import('./pages/WorkflowRunPage'))
const StudioHubPage          = lazy(() => import('./pages/StudioHubPage'))
const StageWorkspacePage     = lazy(() => import('./pages/StageWorkspacePage'))
const JanetMailboxPage       = lazy(() => import('./pages/JanetMailboxPage'))
const MyMailboxPage          = lazy(() => import('./pages/MyMailboxPage'))
const ResearchPage           = lazy(() => import('./pages/ResearchPage'))
const AudiencePage           = lazy(() => import('./pages/AudiencePage'))
const SmsPrivacyPage         = lazy(() => import('./pages/SmsPrivacyPage'))
const ExtensionPrivacyPage   = lazy(() => import('./pages/ExtensionPrivacyPage'))
const SmsTermsPage           = lazy(() => import('./pages/SmsTermsPage'))
const SmsOptInPage           = lazy(() => import('./pages/SmsOptInPage'))
const SmsOptOutPage          = lazy(() => import('./pages/SmsOptOutPage'))

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-warm-100 flex items-center justify-center">
      <div className="text-center">
        <img src="/logo-square.png" alt="Chatrone" className="w-12 h-12 rounded-xl shadow-sm mx-auto mb-4" />
        <div className="w-6 h-6 border-2 border-navy-200 border-t-navy-500 rounded-full animate-spin mx-auto" />
      </div>
    </div>
  )
}

function RouteFallback() {
  const [showRefresh, setShowRefresh] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowRefresh(true), 8000)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (showRefresh) {
      // Auto-reload after 10s total (8s wait + 2s showing message)
      const reload = setTimeout(() => window.location.reload(), 2000)
      return () => clearTimeout(reload)
    }
  }, [showRefresh])

  return (
    <div className="flex-1 flex flex-col items-center justify-center min-h-[50vh] gap-3">
      <div className="w-6 h-6 border-2 border-navy-200 border-t-navy-500 rounded-full animate-spin" />
      {showRefresh && (
        <p className="text-xs text-warm-400 animate-pulse">Refreshing...</p>
      )}
    </div>
  )
}

// AppErrorBoundary — two behaviours:
//   Chunk load errors (stale deploy) → auto-reload once, then show spinner
//   All other React crashes → report to system monitor, show friendly error screen
class AppErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, isChunkError: false, isNetworkError: false, chunkGaveUp: false, errorMessage: null }
  }

  componentDidMount() {
    // If the app stays healthy for a while, clear the stale-deploy reload budget
    // so a FUTURE deploy gets a fresh set of retry attempts. Gated on !hasError
    // so a chunk error can never reset its own cap (which would loop forever).
    this._clearChunkBudget = setTimeout(() => {
      if (!this.state.hasError) { try { sessionStorage.removeItem('_chunk_reload_count') } catch (e) { /* ignore */ } }
    }, 15000)
  }

  componentWillUnmount() {
    if (this._clearChunkBudget) clearTimeout(this._clearChunkBudget)
    if (this._onOnline) window.removeEventListener('online', this._onOnline)
  }

  static getDerivedStateFromError(error) {
    const msg = error?.message || ''
    return {
      hasError: true,
      isChunkError: isStaleChunkError(msg),
      isNetworkError: isTransientNetworkError(msg),
      errorMessage: msg || 'Unknown error',
    }
  }

  componentDidCatch(error, info) {
    if (this.state.isNetworkError) {
      // Connection blip, not a code bug: reloading can't fix it, so don't burn
      // the reload budget or report it. Retry automatically when the browser
      // says the connection is back.
      console.warn('Network error during render:', error.message)
      this._onOnline = () => window.location.reload()
      window.addEventListener('online', this._onOnline, { once: true })
    } else if (this.state.isChunkError) {
      console.warn('Chunk load failed:', error.message)
      // Stale-deploy recovery: a hashed chunk 404s because a new build shipped.
      // Reload to pull the fresh index.html — but CAP attempts so a chunk that's
      // genuinely gone (or a module that throws on import) can't loop forever.
      // The old single-timestamp guard reloaded once then got stuck on the
      // spinner with no escape; this retries a few times, then shows a button.
      const KEY = '_chunk_reload_count'
      let count = 0
      try { count = Number(sessionStorage.getItem(KEY) || '0') } catch (e) { /* ignore */ }
      if (count < 3) {
        try { sessionStorage.setItem(KEY, String(count + 1)) } catch (e) { /* ignore */ }
        setTimeout(() => window.location.reload(), 1200) // let the CDN settle
      } else {
        this.setState({ chunkGaveUp: true }) // stop looping — offer a manual reload
        // Three reloads didn't fix a "stale deploy" — that shouldn't happen, so
        // report it: the message tells us what error was actually looping.
        reportClientError({
          error_message:   `[chunk_reload_loop] ${error.message}`,
          error_stack:     error.stack,
          component_stack: info?.componentStack,
          error_type:      'chunk_reload_loop',
          page_url:        window.location.href,
        })
      }
    } else {
      // Real crash — report to system monitor
      console.error('[AppErrorBoundary]', error.message, error)
      reportClientError({
        error_message:   error.message,
        error_stack:     error.stack,
        component_stack: info?.componentStack,
        error_type:      'react_boundary',
        page_url:        window.location.href,
      })
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children

    if (this.state.isNetworkError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-warm-50 p-6">
          <div className="bg-white rounded-xl border border-warm-200 shadow-sm p-8 max-w-md w-full text-center">
            <div className="w-12 h-12 bg-navy-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📡</span>
            </div>
            <h2 className="text-lg font-bold text-warm-900 mb-2">Connection problem</h2>
            <p className="text-sm text-warm-500 mb-5">
              Couldn't reach the server. This usually clears on its own — the page will retry automatically when the connection is back.
            </p>
            <button onClick={() => window.location.reload()} className="btn-primary text-sm w-full">
              Retry now
            </button>
          </div>
        </div>
      )
    }

    if (this.state.isChunkError) {
      // Auto-retry exhausted — give the user a way out instead of a dead spinner.
      if (this.state.chunkGaveUp) {
        return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-warm-50 p-6">
            <div className="bg-white rounded-xl border border-warm-200 shadow-sm p-8 max-w-md w-full text-center">
              <div className="w-12 h-12 bg-navy-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">↻</span>
              </div>
              <h2 className="text-lg font-bold text-warm-900 mb-2">A new version is ready</h2>
              <p className="text-sm text-warm-500 mb-5">
                We couldn't load it automatically. Reload to get the latest — if it sticks, a hard refresh (⌘/Ctrl + Shift + R) clears the cache.
              </p>
              <button
                onClick={() => { try { sessionStorage.removeItem('_chunk_reload_count') } catch (e) { /* ignore */ } window.location.reload() }}
                className="btn-primary text-sm w-full"
              >
                Reload
              </button>
            </div>
          </div>
        )
      }
      return (
        <div className="flex-1 flex flex-col items-center justify-center min-h-[50vh] gap-3">
          <div className="w-6 h-6 border-2 border-navy-200 border-t-navy-500 rounded-full animate-spin" />
          <p className="text-xs text-warm-400">Loading new version…</p>
        </div>
      )
    }

    // Real crash — show friendly screen
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-warm-50 p-6">
        <div className="bg-white rounded-xl border border-warm-200 shadow-sm p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-lg font-bold text-warm-900 mb-2">Something went wrong</h2>
          <p className="text-sm text-warm-500 mb-4">
            Janet has been notified and is looking into it.
          </p>
          {this.state.errorMessage && (
            <p className="text-xs font-mono text-warm-400 bg-warm-50 rounded-lg p-3 mb-5 text-left break-all">
              {this.state.errorMessage}
            </p>
          )}
          <button
            onClick={() => window.location.reload()}
            className="btn-primary text-sm w-full"
          >
            Reload page
          </button>
        </div>
      </div>
    )
  }
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return <LoadingScreen />
  if (!user) return <Navigate to="/login" replace />
  return children
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return <LoadingScreen />
  if (user) return <Navigate to="/" replace />
  return children
}

// Legacy /janet/fixes deep links (System Monitor emails carry ?id=<proposal_id>,
// verify_ok/verify_broken redirects add &verified=ok) must keep resolving —
// a bare `<Navigate to="/engineering" />` drops the query string entirely.
function EngineeringRedirect() {
  const location = useLocation()
  return <Navigate to={`/engineering${location.search}`} replace />
}

function AppRoutes() {
  return (
    <AppErrorBoundary>
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={
          <PublicRoute><LoginPage /></PublicRoute>
        } />
        <Route path="/onboard/:token" element={<OnboardingIntakePage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/sms/privacy"   element={<SmsPrivacyPage />} />
        <Route path="/extension/privacy" element={<ExtensionPrivacyPage />} />
        <Route path="/sms/terms"     element={<SmsTermsPage />} />
        <Route path="/sms/opt-in"    element={<SmsOptInPage />} />
        <Route path="/sms/opt-out"   element={<SmsOptOutPage />} />
        <Route path="/embed/thread"  element={<EmbedThreadPage />} />
        <Route path="/embed/event"   element={<EmbedEventPage />} />
        <Route path="/share/notes/:token" element={<SharedNotesPage />} />
        {/* The trailer's front door — inert on a non-demo deployment (DEMO-ENVIRONMENT §6) */}
        <Route path="/demo" element={<DemoEntryPage />} />

        {/* Janet in her own window (desktop shell / browser popup) — no app chrome */}
        <Route path="/janet/window" element={<ProtectedRoute><JanetWindowPage /></ProtectedRoute>} />

        {/* Protected routes */}
        <Route element={
          <ProtectedRoute><Layout /></ProtectedRoute>
        }>
          <Route path="/"            element={<HomeNorthStar />} />
          <Route path="/demo/journey" element={<DemoJourneyPage />} />
          {/* Brainstorm + writers' rooms — their own datastore, but inside
              Chatrone's frame like every other page (Aaron 2026-10-14: "we
              want everything to appear to the user as unified"). */}
          <Route element={<ConsoleLayout />}>
            <Route path="/console"                  element={<ConsoleHome />} />
            <Route path="/console/start"            element={<StartBrainstormPage />} />
            <Route path="/console/property/:id"     element={<PropertyPage />} />
            <Route path="/console/project/:id"      element={<ConsoleProjectPage />} />
            <Route path="/console/project/:id/visual" element={<VisualProjectConsole />} />
            <Route path="/console/project/:id/story" element={<StoryProjectConsole />} />
            <Route path="/console/project/:id/studio" element={<StudioProgress />} />
            <Route path="/console/reading" element={<ReadingRoom />} />
            <Route path="/console/guides" element={<GuidesPage />} />
            <Route path="/console/thread/:id"       element={<ConsoleThreadRoom />} />
          </Route>
          <Route path="/home-legacy" element={<Dashboard />} />
          <Route path="/contacts"    element={<ContactsPage />} />
          <Route path="/contacts/:id" element={<ContactDetailPage />} />
          <Route path="/people-of-interest/:id" element={<PersonOfInterestDetailPage />} />
          <Route path="/companies"   element={<CompaniesPage />} />
          <Route path="/companies/:id" element={<CompanyDetailPage />} />
          <Route path="/activity"    element={<ActivityPage />} />
          <Route path="/comms"       element={<CommsPage />} />
          <Route path="/messages"    element={<MessagesPage />} />
          <Route path="/calendar"    element={<CalendarPage />} />
          <Route path="/clients"    element={<ClientsPage />} />
          <Route path="/clients/:id" element={<ClientDetailPage />} />
          <Route path="/properties"    element={<PropertiesPage />} />
          <Route path="/properties/:id" element={<PropertyDetailPage />} />
          <Route path="/projects"      element={<ProjectsPage />} />
          <Route path="/projects/:id"  element={<ProjectDetailPage />} />
          <Route path="/projects/:id/drafts/:draftId" element={<DraftDetailPage />} />
          <Route path="/projects/:projectId/stage/:stageId" element={<StageWorkspacePage />} />
          <Route path="/studio"        element={<StudioBench />} />
          <Route path="/deals"          element={<DealsPage />} />
          <Route path="/deals/:id"      element={<DealWorkroomPage />} />
          <Route path="/deals/:id/full" element={<DealDetailPage />} />
          <Route path="/opportunities"      element={<OpportunitiesPage />} />
          <Route path="/opportunities/:id"  element={<OpportunityDetailPage />} />
          <Route path="/submissions"        element={<SubmissionsPage />} />
          {/* §16: the outbound submission is a first-class openable object */}
          <Route path="/submissions/outbound/:id" element={<OutboundSubmissionDetailPage />} />
          <Route path="/submissions/:id"    element={<SubmissionDetailPage />} />
          <Route path="/inbox"           element={<InboxIntelligencePage />} />
          <Route path="/inbox/gmail"     element={<MyMailboxPage />} />
          <Route path="/inbox/tracking"  element={<EmailTrackingPage />} />
          <Route path="/proposals"        element={<ProposalsPage />} />
          <Route path="/janet/queue"           element={<JanetQueuePage />} />
          <Route path="/janet/attention"       element={<JanetAttentionPage />} />
          <Route path="/janet/chat"            element={<JanetChatPage />} />
          <Route path="/janet/chat/:sessionId" element={<JanetChatPage />} />
          <Route path="/engineering"            element={<EngineeringPage />} />
          <Route path="/janet/fixes"            element={<EngineeringRedirect />} />
          <Route path="/janet/knowledge"       element={<KnowledgeReviewPage />} />
          <Route path="/janet/calibration"     element={<JudgmentReviewPage />} />
          <Route path="/janet/mailbox"         element={<JanetMailboxPage />} />
          <Route path="/janet/interview/:principalKey" element={<JanetInterviewPage />} />
          <Route path="/crew"             element={<Navigate to="/research?tab=industry-people" replace />} />
          <Route path="/people-to-know"   element={<Navigate to="/research?tab=prospects" replace />} />
          <Route path="/intelligence"     element={<Navigate to="/research?tab=signals" replace />} />
          <Route path="/industry-today"   element={<Navigate to="/research?tab=digest" replace />} />
          <Route path="/industry-tracker" element={<Navigate to="/research?tab=tracker" replace />} />
          <Route path="/studio/hub"       element={<StudioHubPage />} />
          <Route path="/studio/design"    element={<StudioPage />} />
          <Route path="/studio/design/:briefId" element={<DesignBriefPage />} />
          <Route path="/workflows/:runId" element={<WorkflowRunPage />} />
          <Route path="/research"         element={<ResearchPage />} />
          <Route path="/audience"         element={<AudiencePage />} />
          <Route path="/tasks"         element={<TasksPage />} />
          <Route path="/tasks/:id"     element={<TaskDetailPage />} />
          <Route path="/finance"                  element={<FinancePage />} />
          <Route path="/finance/invoices/:id"    element={<InvoiceDetail />} />
          <Route path="/files"       element={<FilesPage />} />
          <Route path="/reports"     element={<ReportsPage />} />
          <Route path="/settings"    element={<SettingsPage />} />
          <Route path="/import/google-contacts" element={<GoogleContactsImportPage />} />
          <Route path="/settings/sync-google-contacts" element={<GoogleContactsPushPage />} />
          <Route path="/admin/extract-companies" element={<ExtractCompaniesPage />} />
          <Route path="/admin/merge-companies" element={<MergeCompaniesPage />} />
          <Route path="/admin/merge-documents" element={<MergeDocumentsPage />} />
          <Route path="/admin/bug-reports" element={<BugReportsAdminPage />} />
          <Route path="/admin/bug-reports/:id" element={<BugReportDetailPage />} />
          <Route path="/admin/suggestions" element={<SuggestionsAdminPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="*"            element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
    </AppErrorBoundary>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <ToastProvider>
          <AuthProvider>
            <StepUpProvider>
              <RingCentralProvider>
                <ComposeProvider>
                  <JanetPageProvider>
                    <JanetObjectProvider>
                      <WorkspaceTabsProvider>
                        <DemoProvider>
                          <AppRoutes />
                        </DemoProvider>
                      </WorkspaceTabsProvider>
                    </JanetObjectProvider>
                  </JanetPageProvider>
                </ComposeProvider>
              </RingCentralProvider>
            </StepUpProvider>
          </AuthProvider>
        </ToastProvider>
      </I18nProvider>
    </BrowserRouter>
  )
}
